import type { Candle } from "../market/types";
import { localTime, wallToUtc } from "../market/localTime";
import { PLACE_INFO } from "../market/places";

/**
 * The community vote: per market and trading day every user gives tips — long or short with an exact plan (entry, stop,
 * one to three targets) — and the others vote for or against them. Pure: the rules of a valid plan, the round's day and
 * end, and how a tip fares against the bars after it was given.
 */

export type VoteSide = "long" | "short";

/** A user's access to the vote: cast votes (and see them), see them only, or none (an administrator always votes). */
export const VOTE_ACCESS = ["vote", "view", "off"] as const;
export type VoteAccess = (typeof VOTE_ACCESS)[number];

export interface VotePlan {
  side: VoteSide;
  entry: number;
  stop: number;
  /** One to three targets, each further in the trade's direction. */
  tps: readonly number[];
}

/**
 * Waiting for its entry, running (entered), or final: the stop first, the highest target reached before the stop or
 * the round's end, entered without either by the end (expired), or never entered (missed).
 */
export type VoteStatus = "waiting" | "running" | "stop" | "tp1" | "tp2" | "tp3" | "expired" | "missed";

export interface VoteOutcome {
  status: VoteStatus;
  /** The targets reached so far. */
  reached: number;
  final: boolean;
  /** The result in multiples of the risk (entry to stop): −1 at the stop, the highest target's reward, 0 expired; null while open and when missed. */
  r: number | null;
}

export const MAX_TARGETS = 3;

/** The direction of a side: +1 long, −1 short. */
const dirOf = (side: VoteSide) => (side === "long" ? 1 : -1);

/** Why a plan is not valid (a message key), else null: positive prices, the stop behind the entry, the targets ahead and in order. */
export function planError(p: VotePlan): string | null {
  const prices = [p.entry, p.stop, ...p.tps];
  if (p.tps.length < 1 || p.tps.length > MAX_TARGETS) return "err.vote.targets";
  if (!prices.every((x) => Number.isFinite(x) && x > 0)) return "err.vote.price";
  const dir = dirOf(p.side);
  if (dir * (p.entry - p.stop) <= 0) return "err.vote.stop";
  const ahead = [p.entry, ...p.tps];
  return p.tps.every((tp, k) => dir * (tp - ahead[k]) > 0) ? null : "err.vote.order";
}

/** The foreign-exchange trading day ends at 17:00 New York: shifted by 7 hours it starts at midnight. */
const DAY_SHIFT = 7 * 3_600_000;
const NY = PLACE_INFO.newYork.timeZone;

/** The round a time belongs to: the trading day ending 17:00 New York ("yyyy-mm-dd"). */
export const voteDay = (t: number): string => localTime(t + DAY_SHIFT, NY).date;

/** When a round ends: 17:00 New York on its day. */
export function voteDayEnd(day: string): number {
  return wallToUtc(Date.parse(`${day}T00:00:00Z`) + 86_400_000, NY) - DAY_SHIFT;
}

/** A target's reward in multiples of the risk (the entry's distance to the stop). */
export const rewardOf = (p: VotePlan, tp: number): number => Math.abs(tp - p.entry) / Math.abs(p.entry - p.stop);

/** The reward of the highest target reached, in multiples of the risk (−1: none reached). */
function rOf(p: VotePlan, reached: number): number {
  return reached === 0 ? -1 : rewardOf(p, p.tps[reached - 1]);
}

/** Where a vote stands after the bars seen so far. */
interface Walk {
  filled: boolean;
  reached: number;
  stopped: boolean;
}

const stopHit = (p: VotePlan, b: Candle) => (dirOf(p.side) > 0 ? b.l <= p.stop : b.h >= p.stop);
const targetsAt = (p: VotePlan, b: Candle) => p.tps.filter((tp) => (dirOf(p.side) > 0 ? b.h >= tp : b.l <= tp)).length;

/**
 * One bar of a vote: entered when the bar reaches its entry (that bar counts its stop, not its targets — which came
 * first in it is unknown); entered, the stop is checked before the targets (a bar reaching both counts the stop).
 */
function step(p: VotePlan, w: Walk, b: Candle): void {
  if (!w.filled) {
    w.filled = b.l <= p.entry && p.entry <= b.h;
    w.stopped = w.filled && stopHit(p, b);
    return;
  }
  if (stopHit(p, b)) {
    w.stopped = true;
    return;
  }
  w.reached = Math.max(w.reached, targetsAt(p, b));
}

/** A finished walk: the stop after the highest target reached, all targets, or the round's end. */
function finalOutcome(p: VotePlan, w: Walk): VoteOutcome {
  if (!w.filled) return { status: "missed", reached: 0, final: true, r: null };
  if (w.reached > 0) return { status: `tp${w.reached}` as VoteStatus, reached: w.reached, final: true, r: rOf(p, w.reached) };
  return { status: w.stopped ? "stop" : "expired", reached: 0, final: true, r: w.stopped ? -1 : 0 };
}

/**
 * How a vote fares: the bars opening from `from` (when it was cast — a bar already open then is not used, its range
 * includes what came before) to the round's `end`, as far as they are known (`seenUntil`). Final at its stop, at its
 * last target, or once the bars reach the end.
 */
export function evaluateVote(p: VotePlan, bars: readonly Candle[], from: number, end: number, seenUntil: number): VoteOutcome {
  const w: Walk = { filled: false, reached: 0, stopped: false };
  for (const b of bars) {
    if (b.t < from) continue;
    if (b.t >= end || w.stopped || w.reached === p.tps.length) break;
    step(p, w, b);
  }
  if (w.stopped || w.reached === p.tps.length || seenUntil >= end) return finalOutcome(p, w);
  return { status: w.filled ? "running" : "waiting", reached: w.reached, final: false, r: null };
}
